/**
 * Kai mail-review artifact types — mirror
 * `tideway-kai/src/orchestrator/artifacts/mail_review_snapshot.rs`.
 */

import type { GraphMailAttachmentSummary } from "./kaiToolDownloads";
import { isGraphMailDocumentFilename } from "@/lib/graphMailDocuments";

export interface KaiMailReviewFilters {
  from_address?: string | null;
  to_address?: string | null;
  received_from?: string | null;
  received_to?: string | null;
  source_id?: string | null;
  file_id?: string | null;
  consignee?: string | null;
  sales_person?: string | null;
  category?: string | null;
  subject_contains?: string | null;
}

export interface KaiMailReviewMessage {
  message_id: string;
  mailbox_id?: string | null;
  subject?: string | null;
  from_address?: string | null;
  received_at?: string | null;
  category?: string | null;
  booking_source_id?: string | null;
  file_id?: string | null;
  digest?: string | null;
  has_attachments: boolean;
}

export interface KaiMailReviewThread {
  conversation_id?: string | null;
  subject?: string | null;
  latest_received_at?: string | null;
  message_count: number;
  messages: KaiMailReviewMessage[];
}

export interface KaiMailReviewSnapshot {
  title?: string | null;
  filters: KaiMailReviewFilters;
  threads: KaiMailReviewThread[];
  total_messages?: number;
  footnotes?: string[];
  /** Downloadable document attachments across the listed messages. */
  graph_attachments?: GraphMailAttachmentSummary[];
}

export interface KaiMailReviewSnapshotRefSpec {
  id: string;
  artifact?: unknown;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

function optString(v: unknown): string | null {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

function parseMessage(row: unknown): KaiMailReviewMessage | null {
  if (!isRecord(row)) return null;
  const messageId = typeof row.message_id === "string" ? row.message_id.trim() : "";
  if (!messageId) return null;
  return {
    message_id: messageId,
    mailbox_id: optString(row.mailbox_id),
    subject: optString(row.subject),
    from_address: optString(row.from_address),
    received_at: optString(row.received_at),
    category: optString(row.category),
    booking_source_id: optString(row.booking_source_id),
    file_id: optString(row.file_id),
    digest: optString(row.digest),
    has_attachments: row.has_attachments === true,
  };
}

function parseThread(row: unknown): KaiMailReviewThread | null {
  if (!isRecord(row)) return null;
  const messages = Array.isArray(row.messages)
    ? row.messages
        .map(parseMessage)
        .filter((m): m is KaiMailReviewMessage => m != null)
    : [];
  if (messages.length === 0) return null;
  return {
    conversation_id: optString(row.conversation_id),
    subject: optString(row.subject),
    latest_received_at: optString(row.latest_received_at),
    message_count:
      typeof row.message_count === "number" ? row.message_count : messages.length,
    messages,
  };
}

/** Normalise the artifact's passthrough `graph_attachments` into the shared
 * {@link GraphMailAttachmentSummary} shape the attachments card renders. */
function parseAttachments(raw: unknown): GraphMailAttachmentSummary[] {
  if (!Array.isArray(raw)) return [];
  const out: GraphMailAttachmentSummary[] = [];
  const seen = new Set<string>();
  for (const ref of raw) {
    if (!isRecord(ref)) continue;
    const mailboxId = optString(ref.mailbox_id);
    const messageId = optString(ref.message_id);
    const attachmentId = optString(ref.attachment_id);
    const filename = optString(ref.filename);
    if (!mailboxId || !messageId || !attachmentId || !filename) continue;
    if (!isGraphMailDocumentFilename(filename)) continue;
    const key = `${messageId}:${attachmentId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      key,
      mailboxId,
      messageId,
      attachmentId,
      filename,
      contentType: optString(ref.content_type),
      sizeBytes: typeof ref.size_bytes === "number" ? ref.size_bytes : null,
      messageSubject: optString(ref.message_subject),
      category: optString(ref.category),
      receivedAt: optString(ref.received_at),
      downloadable: ref.downloadable === true,
    });
  }
  return out;
}

/** Parse + validate a `mail_review_snapshot` fenced body or artifact payload. */
export function parseMailReviewSnapshotSpec(
  body: string,
): KaiMailReviewSnapshot | null {
  try {
    const parsed: unknown = JSON.parse(body);
    if (!isRecord(parsed)) return null;
    if (!Array.isArray(parsed.threads)) return null;

    const threads = parsed.threads
      .map(parseThread)
      .filter((t): t is KaiMailReviewThread => t != null);

    const filtersRaw = isRecord(parsed.filters) ? parsed.filters : {};
    const footnotes = Array.isArray(parsed.footnotes)
      ? parsed.footnotes.filter(
          (f): f is string => typeof f === "string" && f.trim() !== "",
        )
      : [];

    return {
      title: optString(parsed.title),
      filters: {
        from_address: optString(filtersRaw.from_address),
        to_address: optString(filtersRaw.to_address),
        received_from: optString(filtersRaw.received_from),
        received_to: optString(filtersRaw.received_to),
        source_id: optString(filtersRaw.source_id),
        file_id: optString(filtersRaw.file_id),
        consignee: optString(filtersRaw.consignee),
        sales_person: optString(filtersRaw.sales_person),
        category: optString(filtersRaw.category),
        subject_contains: optString(filtersRaw.subject_contains),
      },
      threads,
      total_messages:
        typeof parsed.total_messages === "number"
          ? parsed.total_messages
          : undefined,
      footnotes,
      graph_attachments: parseAttachments(parsed.graph_attachments),
    };
  } catch {
    return null;
  }
}

/** Parse a compact `mail_review_snapshot_ref` fence body `{ "id": "..." }`. */
export function parseMailReviewSnapshotRefSpec(
  body: string,
): KaiMailReviewSnapshotRefSpec | null {
  try {
    const parsed: unknown = JSON.parse(body);
    if (!isRecord(parsed)) return null;
    const id = typeof parsed.id === "string" ? parsed.id.trim() : "";
    if (!id) return null;
    return { id, artifact: parsed.artifact };
  } catch {
    return null;
  }
}
